import React, { useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { Spinner } from './Primitives';
import {
  ProductBadges,
  PowerStateBadge,
  IdentityBadge,
} from './VmMeta';
import VmDetailFields from './VmDetailFields';
import VmActions from './VmActions';
import VmIpAddress from './VmIpAddress';
import { useVmDetail } from '../hooks/useVmDetail';
import { powerStateMeta } from '../utils/constants';
import { formatMemoryMb } from '../utils/format';

function VmTableRow({ vm, catalogWarnings, op, busy, onRequestAction, onDismissOp }) {
  const [expanded, setExpanded] = useState(false);
  const { detail, loading } = useVmDetail(vm.catalogId, { enabled: expanded });
  const merged = detail ? { ...vm, ...detail } : vm;
  const meta = powerStateMeta(vm.powerState);
  const res = vm.resources || {};

  return (
    <>
      <tr className="border-t border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-900/40">
        <td className="px-3 py-2.5">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={expanded ? `Nascondi dettagli ${vm.name}` : `Mostra dettagli ${vm.name}`}
            className="flex items-center gap-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
          >
            <FaChevronDown
              size={10}
              aria-hidden="true"
              className={`text-gray-400 transition-transform motion-reduce:transition-none ${expanded ? 'rotate-180' : ''}`}
            />
            <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} aria-hidden="true" />
            <span className="font-medium text-gray-900 dark:text-white">{vm.name}</span>
          </button>
        </td>
        <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-300">
          {vm.environment || '—'}
        </td>
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-1.5">
            <ProductBadges vm={vm} />
            <IdentityBadge vm={vm} catalogWarnings={catalogWarnings} />
          </div>
        </td>
        <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-300">
          {vm.role || '—'}
        </td>
        <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-300">
          <VmIpAddress vm={vm} compact />
        </td>
        <td className="whitespace-nowrap px-3 py-2.5 text-sm text-gray-600 dark:text-gray-300">
          {res.vcpus ?? '—'} vCPU · {formatMemoryMb(res.memoryMb)}
        </td>
        <td className="px-3 py-2.5">
          <PowerStateBadge state={vm.powerState} />
        </td>
        <td className="px-3 py-2.5">
          <VmActions
            vm={vm}
            catalogWarnings={catalogWarnings}
            op={op}
            busy={busy}
            onRequestAction={onRequestAction}
            onDismissOp={onDismissOp}
            size="small"
          />
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50 dark:bg-gray-900/40">
          <td colSpan={8} className="px-4 py-3">
            {loading && (
              <div className="mb-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <Spinner className="h-3.5 w-3.5" /> Caricamento dettagli…
              </div>
            )}
            {vm.description && (
              <p className="mb-3 text-xs text-gray-600 dark:text-gray-300">{vm.description}</p>
            )}
            <VmDetailFields vm={merged} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Vista tabellare compatta dell'elenco VM di un gruppo. Scorre orizzontalmente
 * su schermi stretti senza far scorrere l'intera pagina.
 */
export default function VmTable({
  vms,
  catalogWarnings,
  operations,
  isBusy,
  onRequestAction,
  onDismissOp,
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-700">
      <table className="w-full min-w-[720px] border-collapse text-left">
        <thead>
          <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-900/50 dark:text-gray-400">
            <th scope="col" className="px-3 py-2 font-semibold">Nome</th>
            <th scope="col" className="px-3 py-2 font-semibold">Ambiente</th>
            <th scope="col" className="px-3 py-2 font-semibold">Prodotti</th>
            <th scope="col" className="px-3 py-2 font-semibold">Ruolo</th>
            <th scope="col" className="px-3 py-2 font-semibold">IP guest</th>
            <th scope="col" className="px-3 py-2 font-semibold">Risorse</th>
            <th scope="col" className="px-3 py-2 font-semibold">Stato</th>
            <th scope="col" className="px-3 py-2 font-semibold">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {vms.map((vm) => (
            <VmTableRow
              key={vm.catalogId}
              vm={vm}
              catalogWarnings={catalogWarnings}
              op={operations[vm.catalogId]}
              busy={isBusy(vm.catalogId)}
              onRequestAction={onRequestAction}
              onDismissOp={onDismissOp}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
